import { motion } from "framer-motion";
import { PartyPopper, Tag, UserPlus, Compass, KeySquare, CalendarClock } from "lucide-react";

const fadeIn = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

const cases = [
    { icon: PartyPopper, title: "Grand opening", body: "Fill the room on day one. Book the week of your launch and hit every commuter route into the suburb.", tag: "1 week · 1–2 screens" },
    { icon: Tag, title: "Sale or promotion", body: "Move stock, drive traffic. Short bursts of visibility timed with a discount window or a bank holiday.", tag: "1–2 weeks · 2–4 screens" },
    { icon: UserPlus, title: "Recruitment push", body: "Talent notices billboards. Put the role in front of the neighbourhoods you want to hire from.", tag: "2 weeks · 1–3 screens" },
    { icon: Compass, title: "Local awareness", body: "Stay top-of-mind in one city. A steady presence on the routes your customers already travel.", tag: "4+ weeks · 1–2 screens" },
    { icon: KeySquare, title: "Property listing", body: "Feature a new listing or a project launch. Pinpoint the postcodes buyers are searching in.", tag: "1–3 weeks · 1–2 screens" },
    { icon: CalendarClock, title: "Event countdown", body: "Gig, market, festival, product drop. Count down the days on rotation until doors open.", tag: "1–2 weeks · 2–5 screens" },
];

export const UseCases = ({ onBegin }) => {
    return (
        <section
            id="use-cases"
            data-testid="landing-use-cases"
            className="relative py-14 lg:py-20"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeIn(0)} className="max-w-3xl mb-12">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-6 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" />
                        03 / Use cases
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                        Whatever you&apos;re here for,<br />
                        <span className="text-slate-500">there&apos;s a play for it.</span>
                    </h2>
                    <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
                        Every campaign brief is different. Here&apos;s the shape of a few we
                        see often — pick the closest one to yours and start there.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
                    {cases.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <motion.div
                                key={c.title}
                                {...fadeIn(0.05 * i)}
                                data-testid={`landing-usecase-${i}`}
                                className="bg-[#0F1E3B] hover:bg-[#1B2E55] p-8 lg:p-10 transition-colors duration-500 flex flex-col"
                            >
                                <Icon className="text-[#FF5A00] mb-5" size={22} strokeWidth={1.5} />
                                <div className="font-display font-black text-xl text-white tracking-tight">
                                    {c.title}
                                </div>
                                <div className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">
                                    {c.body}
                                </div>
                                <div className="mt-6 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    {c.tag}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div {...fadeIn(0.35)} className="mt-10">
                    <button
                        onClick={onBegin}
                        data-testid="landing-usecase-cta"
                        className="inline-flex items-center gap-3 bg-[#FF5A00] hover:bg-[#E65200] text-white px-7 py-3.5 text-sm font-semibold uppercase tracking-widest transition-all duration-300"
                    >
                        Put Me On A Billboard
                        <span aria-hidden>→</span>
                    </button>
                </motion.div>
            </div>
        </section>
    );
};
